import { Moneybuy } from '@comicrelief/data-models/schemas/payments/models/Cart';
import { JOURNEY_TYPES } from '@/constants/constants';

const { SINGLE } = JOURNEY_TYPES;

export interface GtmMoneybuyItem {
  item_id: string,
  item_name: string,
  affiliation: string,
  price?: number,
  quantity: 1,
  item_brand: string,
  item_category?: string,
  item_category2: string,
  // Optional: used for Giftaid status only present once it's been set in the journey
  item_category3?: string,
  item_list_id: string,
  item_list_name: string,
  index: number,
}

interface ProcessItemProps {
  theseMoneybuys: Moneybuy[] | undefined,
  thisJourneyName: string | undefined,
  cartId: string | undefined,
  position: number | null | undefined,
  amount: number | null | undefined,
  giftAid: boolean | undefined,
  referringUrl: string | undefined
}

// Helper function to extract the exact 'page' (not the *full* path) from any widget-set state, if set:
function getSiteURLPage(url:string | undefined) {
  let output;

  // If a referring URL has been passed, process accordingly:
  if (url) {
    // Grab the only part of the URL we care about:
    const thisPathname = new URL(url).pathname;

    // No subdirectories = homepage
    if (thisPathname === '/') {
      output = 'homepage';
    } else {
      // Otherwise, we've got some processing to do, first dividing up the path:
      output = thisPathname.split('/');
      // And then grabbing the last subdirectory, ignoring the trailing slash:
      output = output[output.length - 2];
    }

    // And prefix as requested:
    output = 'crcom_' + output;
  } else {
    // Else, generic fallback:
    output = 'donate_homepage';
  }

  return output;
}

export function processItems(params:Partial<ProcessItemProps>) {
  const {
    theseMoneybuys,
    thisJourneyName,
    cartId,
    position,
    amount,
    giftAid,
    referringUrl,
  } = params;
  // Check we've got what we need first:
  if (!theseMoneybuys) return [];

  let output: GtmMoneybuyItem[] = [];

  const thisUrl = getSiteURLPage(referringUrl);

  const selectedJourney = thisJourneyName === SINGLE ? 'single-donations' : 'regular-donations';

  // These work as flags to indicate a _selected_ moneybuy, meaning we only need to create a single item here.
  // Also, check position isn't null specifically to ensure our zero-indexed 'position' isn't equating to 'false'
  // as a false positive when the '0' representing manual entry is passed:
  if (position !== null && position !== undefined
    && amount !== null && amount !== undefined) {
    // As mentioned, a position of -1 always represents the manual entry:
    const isManualEntry = position === -1;

    const thisSingleMoneyBuyItem: GtmMoneybuyItem = {
      item_id: isManualEntry ? 'manual-entry' : `moneybuy-${amount}`,
      item_name: isManualEntry ? 'manual-entry' : `moneybuy-${amount}`,
      affiliation: 'cr-donation-v2',
      price: amount,
      quantity: 1,
      item_brand: 'comicrelief',
      item_category: cartId,
      item_category2: selectedJourney,
      // Only set this when we're specifically passing it:
      ...(giftAid !== null && { item_category3: `giftaid-${giftAid ? 'yes' : 'no'}` }),
      item_list_id: thisUrl,
      item_list_name: thisUrl,
      // Convert our zero-indexed position to a 1-indexed value
      index: (position ?? 0) + 1
    };

    output = [thisSingleMoneyBuyItem];
  } else {
    // Else, we need to create items for everything visible on the homepage:
    let allMoneybuyItems: GtmMoneybuyItem[] = [];

    // Create our 'manual entry' by hand:
    const thisManualItem: GtmMoneybuyItem = {
      item_id: 'manual-entry',
      item_name: 'manual-entry',
      affiliation: 'cr-donation-v2',
      // As requested, even though the manual entry actually shows the value for the currently-selected Moneybuy
      price: 0.00,
      quantity: 1,
      item_brand: 'comicrelief',
      item_category: cartId,
      item_category2: selectedJourney,
      item_list_id: thisUrl,
      item_list_name: thisUrl,
      index: 0
    };

    // Create an array of 'items' supplied, one per Moneybuy:
    allMoneybuyItems = theseMoneybuys.map((thisMoneybuy, index) => ({
      item_id: `moneybuy-${thisMoneybuy.amount}`,
      item_name: `moneybuy-${thisMoneybuy.amount}`,
      affiliation: 'cr-donation-v2',
      price: thisMoneybuy.amount,
      quantity: 1,
      item_brand: 'comicrelief',
      item_category: cartId,
      item_category2: selectedJourney,
      item_list_id: thisUrl,
      item_list_name: thisUrl,
      // As the above manual entry is always '0', Moneybuys start at 1, so tweak the map index accordingly
      index: index + 1
    } as GtmMoneybuyItem));

    // Combine these into a single array:
    output = output.concat(thisManualItem, allMoneybuyItems);
  }

  return output;
}

export function buildDataLayerEvent(
  eventName: string,
  allMoneybuyItems: object[] | undefined,
  currency: string,
  value: number | undefined = undefined,
  payment_type: string | undefined = undefined,
  transaction_id: string | undefined = undefined
): Record<string, unknown> {
  // Having to handle _some_ edgecases in our lovely generic function:
  const includePaymentType = payment_type && eventName === 'add_payment_info';
  const includeTransactionId = transaction_id && eventName === 'purchase';

  return {
    event: eventName,
    ecommerce: {
      items: allMoneybuyItems,
      currency,
      ...(value && { value }),
      ...(includePaymentType && { payment_type }),
      ...(includeTransactionId && { transaction_id })
    }
  };
}

interface PayinEventProps {
  eventName: string,
  currency: string,
  cartId: string,
  value: number | undefined,
  audience: 'school' | 'work' | 'public' | undefined,
  paymentType: string | undefined,
  transactionId: string | undefined,
  activities: string[] | undefined,
  referringUrl: string | undefined,
}

// Since Payin has different requirements in terms of its DataLayer events, it needs
// its own bespoke function, rather than further mangling buildDataLayerEvent above:
export function buildDataLayerPayinEvent(params:Partial<PayinEventProps>): Record<string, unknown> {
  const {
    eventName,
    currency,
    cartId,
    value,
    audience,
    paymentType,
    transactionId,
    activities,
    referringUrl,
  } = params;
  // Having to handle _some_ edgecases in our lovely generic function:
  const includePaymentType = paymentType && eventName === 'add_payment_info';
  const includeTransactionId = transactionId && eventName === 'purchase';
  const thisUrl = getSiteURLPage(referringUrl);

  // If we've got activities set, make a pipe-delimited string of them, else leave undefined to exclude:
  const allActivities =  (activities?.length ? activities?.join('|') : undefined);

  return {
    event: eventName,
    ecommerce: {
      currency,
      ...(value && { value }),
      items: [{
        item_id: audience,
        item_name: audience,
        affiliation: 'cr-payin',
        item_brand: 'comicrelief',
        item_category: cartId,
        item_category2: 'fundraising',
        item_list_id: thisUrl,
        item_list_name: thisUrl,
        index: 0, // Always '0' since we only have form of input; no 'Moneybuys' to choose from:
        price: (value ?? 0.00),
        ...(audience && { item_id: audience, item_name: audience }),
        ...(value && { quantity: 1 }),
        ...(allActivities && { item_fundraising_activity: allActivities }),
      }],
      ...(includePaymentType && { paymentType }),
      ...(includeTransactionId && { transactionId })
    }
  };
}
